/**
 * Native Status Bar Hook
 * Controls iOS/Android status bar appearance based on current screen context
 */

import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

type StatusBarTheme = 'light' | 'dark' | 'call' | 'transparent';

interface StatusBarConfig {
  theme?: StatusBarTheme;
  backgroundColor?: string;
  overlay?: boolean;
}

const isNative = Capacitor.isNativePlatform();

// FLICKER FIX (2026-06-08): On iOS, toggling setOverlaysWebView resizes the
// WKWebView's drawing surface, which exposes the native UIWindow background
// for one frame → visible white/green flash when navigating between the call
// screen (overlay=true) and normal app pages (overlay=false). The previous
// coarse `lastAppliedKey` dedupe still re-issued every native call whenever
// ANY field changed, and `force: isOverlay` re-toggled overlay on every
// mount of PstnCallScreen. Track each native primitive separately so we only
// call into the bridge when its specific value actually changes.
let lastStyle: Style | null = null;
let lastOverlay: boolean | null = null;
let lastAndroidBg: string | null = null;
let lastLightBarsClass: boolean | null = null;
let applyChain: Promise<void> = Promise.resolve();

const assertAndroidSystemBarMeta = (theme: StatusBarTheme) => {
  if (Capacitor.getPlatform() !== 'android' || typeof document === 'undefined') return;
  const lightBackground = theme === 'light' || theme === 'transparent';
  if (lastLightBarsClass === lightBackground) return;
  lastLightBarsClass = lightBackground;
  document.documentElement.classList.toggle('elefa-native-light-bars', lightBackground);
  document.documentElement.classList.toggle('elefa-native-dark-bars', !lightBackground);
};

const applyStatusBarConfigNow = async (config: StatusBarConfig) => {
  if (!isNative) return;

  try {
    const { theme = 'dark', backgroundColor, overlay = false } = config;
    assertAndroidSystemBarMeta(theme);

    const isAndroid = Capacitor.getPlatform() === 'android';
    const targetStyle: Style =
      theme === 'light' || theme === 'transparent' ? Style.Light : Style.Dark;

    // Apply background FIRST on Android. On Android 12+ a fully-transparent
    // status bar with overlaysWebView=false can render with undefined
    // appearance on OEM skins; a solid color stabilises the appearance flag.
    if (isAndroid) {
      const androidBg = theme === 'call' || theme === 'transparent'
        ? '#00000000'
        : (backgroundColor || (theme === 'light' ? '#FFFFFF' : '#0d1119'));
      if (androidBg !== lastAndroidBg) {
        lastAndroidBg = androidBg;
        await StatusBar.setBackgroundColor({ color: androidBg });
      }
    }

    if (targetStyle !== lastStyle) {
      lastStyle = targetStyle;
      await StatusBar.setStyle({ style: targetStyle });
    }

    // Only toggle overlay when it actually changes - this is the source of
    // the WKWebView resize flicker on iOS.
    if (overlay !== lastOverlay) {
      lastOverlay = overlay;
      await StatusBar.setOverlaysWebView({ overlay });
      // Re-assert style after overlay change ONLY on Android, where the
      // APPEARANCE_LIGHT_STATUS_BARS flag can drop. On iOS this would just
      // cause another bridge call for no benefit.
      if (isAndroid) {
        setTimeout(() => {
          StatusBar.setStyle({ style: targetStyle }).catch(() => {});
        }, 30);
      }
    }
  } catch (error) {
    console.warn('Status bar configuration failed:', error);
  }
};

const applyStatusBarConfig = (config: StatusBarConfig) => {
  applyChain = applyChain.then(() => applyStatusBarConfigNow(config), () => applyStatusBarConfigNow(config));
  return applyChain;
};

/**
 * Hook to control status bar appearance
 * @param theme - 'light' for light backgrounds, 'dark' for dark backgrounds, 'call' for call screen
 * @param backgroundColor - Optional background color (Android)
 */
export const useNativeStatusBar = (
  theme: StatusBarTheme = 'light',
  backgroundColor?: string
) => {
  useEffect(() => {
    const isOverlay = theme === 'call' || theme === 'transparent';
    applyStatusBarConfig({
      theme,
      backgroundColor:
        backgroundColor ||
        (theme === 'call' ? '#0d1119' : theme === 'transparent' ? '#00000000' : undefined),
      overlay: isOverlay,
    });
  }, [theme, backgroundColor]);
};


/**
 * Show the status bar
 */
export const showStatusBar = async () => {
  if (!isNative) return;
  try {
    await StatusBar.show();
  } catch (error) {
    console.warn('Failed to show status bar:', error);
  }
};

/**
 * Hide the status bar (for immersive experiences)
 */
export const hideStatusBar = async () => {
  if (!isNative) return;
  try {
    await StatusBar.hide();
  } catch (error) {
    console.warn('Failed to hide status bar:', error);
  }
};

export default useNativeStatusBar;
